import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { toast } from "react-hot-toast";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { caps } from "../fmt.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import {
  FormCheckboxOption,
  FormOption,
  FormOptions,
} from "../forms/FormTextField.tsx";
import { PremiumContentNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { CannotRemoveNote } from "./FormNote.tsx";
import { useGitContext } from "./ModelContext.tsx";
import { RichContent } from "./PremiumContent.tsx";
import { PreviewBanner } from "./StashPreviewBanner.tsx";
import { formOption } from "./style.css.ts";

const DELIMITER = ".";

function toCompositeId(ref: SpaceGits.TreatmentRef) {
  return `${ref.locationId}${DELIMITER}${ref.id}`;
}

function fromCompositeId(compositeId: string) {
  const [locationId, id] = compositeId.split(DELIMITER) as [string, string];
  return { locationId, id };
}

function toTreatmentRefs(repairCompositeIds: string[]) {
  return repairCompositeIds.map(fromCompositeId);
}

export function ChooseTreatmentsDialog() {
  const git = useGitContext();
  const { mode } = git.gang;
  const dialog = useDialogContext();
  const treatmentLocations = git.ruleset.treatmentLocations.toArray();
  const initialCompositeIds = git.treatmentRefs.map(toCompositeId);
  const canRemoveExisting = git.can(EditMode.REMOVE, "treatments");

  const { form, submitName } = useForm({
    defaultValues: {
      isPaid: false,
      treatmentCompositeIds: initialCompositeIds,
    },

    async onSubmit({ values }) {
      const treatmentRefs = toTreatmentRefs(values.treatmentCompositeIds);
      const nextGit = git.addTreatments(treatmentRefs, values.isPaid);
      return await database.gangs.updateOne(nextGit.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });

  const values = useStoreState(form, (store) => store.values);
  const treatmentRefs = toTreatmentRefs(values.treatmentCompositeIds);
  const draftGit = git.addTreatments(treatmentRefs, values.isPaid);

  return (
    <FormDialog overline="Bad Doc's" heading="Treatment Waiver" form={form}>
      <PreviewBanner draftGang={draftGit.gang} where="at the clinic" />

      <PremiumContentNote />

      {mode.isCampaignMode && git.can(EditMode.ADD, "treatments") && (
        <FormOptions label="Treatment Fees">
          <FormCheckboxOption
            label="Gone private?"
            description="Select if you rolled for treatment twice and picked your preferred option."
            name={form.names.isPaid}
            type="checkbox"
          />
        </FormOptions>
      )}

      {treatmentLocations.map((location) => {
        return (
          <FormOptions label={location.name} key={location.id}>
            {location.treatments
              .toArray()
              .filter((treatment) => {
                return (
                  mode.isCampaignMode ||
                  !treatment.effects.some((e) => e.type === "REMOVE_MODEL")
                );
              })
              .map((treatment) => {
                const { id, name, description, location } = treatment;
                const compositeId = toCompositeId({
                  id,
                  locationId: location.id,
                });

                const isDisabled =
                  !canRemoveExisting &&
                  initialCompositeIds.includes(compositeId);

                return (
                  <FormOption
                    type="checkbox"
                    key={compositeId}
                    name={form.names.treatmentCompositeIds}
                    value={compositeId}
                    label={name}
                    info={caps(values.isPaid ? location.cost : 0)}
                    disabled={isDisabled}
                    description={
                      <>
                        <RichContent>{description}</RichContent>

                        {isDisabled && (
                          <div className={formOption.extras}>
                            <CannotRemoveNote what="TREATMENT" />
                          </div>
                        )}
                      </>
                    }
                  />
                );
              })}
          </FormOptions>
        );
      })}

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
